// V96 — Bell de notifications pharmacien temps-réel.
// Réutilise le badge logique d'inbox mais cible la table pharmacist_notifications
// avec souscription realtime (pas de polling).

import { cn } from '@pharmaconnect/ui/web'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { supabase } from '../lib/supabase'
import {
  countUnreadPharmacistNotifications,
  listPharmacistNotifications,
  markNotificationRead,
  type NotifSeverity,
  type PharmacistNotification,
} from '../services/pharmacistNotifications'
import { useAuthStore } from '../stores/authStore'

const SEVERITY_DOT: Record<NotifSeverity, string> = {
  low: 'bg-info',
  medium: 'bg-amber-400',
  high: 'bg-offline',
  critical: 'bg-offline animate-pulse',
}

const SEVERITY_BG: Record<NotifSeverity, string> = {
  low: 'bg-info text-white',
  medium: 'bg-amber-400 text-neutral-900',
  high: 'bg-offline text-white',
  critical: 'bg-offline text-white',
}

const fmtRelative = (iso: string): string => {
  const ms = Date.now() - new Date(iso).getTime()
  const m = Math.floor(ms / 60_000)
  if (m < 1) return 'à l\'instant'
  if (m < 60) return `il y a ${m} min`
  const h = Math.floor(m / 60)
  if (h < 24) return `il y a ${h} h`
  const d = Math.floor(h / 24)
  if (d < 7) return `il y a ${d} j`
  return new Date(iso).toLocaleDateString('fr-FR')
}

export function NotificationsBell() {
  const profile = useAuthStore((s) => s.profile)
  const pharmacyId = profile?.pharmacy_id ?? ''
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const popRef = useRef<HTMLDivElement | null>(null)
  const isPharmacist = profile?.role === 'owner' || profile?.role === 'manager'

  const countQ = useQuery({
    queryKey: ['pharmacist-notif-count', pharmacyId],
    queryFn: countUnreadPharmacistNotifications,
    enabled: Boolean(pharmacyId) && isPharmacist,
    refetchInterval: 60_000,
  })

  const listQ = useQuery({
    queryKey: ['pharmacist-notif-list', pharmacyId],
    queryFn: () => listPharmacistNotifications(false, 15),
    enabled: open && Boolean(pharmacyId) && isPharmacist,
    staleTime: 10_000,
  })

  // Realtime subscription : tout INSERT déclenche un refetch
  useEffect(() => {
    if (!pharmacyId || !isPharmacist) return
    const channel = supabase
      .channel(`pharmacist-notif:${pharmacyId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'pharmacist_notifications',
          filter: `pharmacy_id=eq.${pharmacyId}`,
        },
        () => {
          void qc.invalidateQueries({ queryKey: ['pharmacist-notif-count', pharmacyId] })
          void qc.invalidateQueries({ queryKey: ['pharmacist-notif-list', pharmacyId] })
        },
      )
      .subscribe()
    return () => {
      void supabase.removeChannel(channel)
    }
  }, [pharmacyId, isPharmacist, qc])

  // Click outside ferme le pop
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  if (!isPharmacist) return null

  const count = countQ.data ?? 0
  const items = listQ.data ?? []

  const handleClickItem = async (n: PharmacistNotification) => {
    if (!n.read_at) {
      await markNotificationRead(n.id).catch(() => {})
      void qc.invalidateQueries({ queryKey: ['pharmacist-notif-count', pharmacyId] })
      void qc.invalidateQueries({ queryKey: ['pharmacist-notif-list', pharmacyId] })
    }
    setOpen(false)
    if (n.link_route) navigate(n.link_route)
  }

  const handleMarkAllRead = async () => {
    await markNotificationRead().catch(() => {})
    void qc.invalidateQueries({ queryKey: ['pharmacist-notif-count', pharmacyId] })
    void qc.invalidateQueries({ queryKey: ['pharmacist-notif-list', pharmacyId] })
  }

  return (
    <div ref={popRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative rounded-md p-2 text-neutral-300 transition hover:bg-white/10 hover:text-white"
        aria-label="Notifications"
        data-testid="notifications-bell"
      >
        🔔
        {count > 0 ? (
          <span
            className={cn(
              'absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold tabular-nums',
              count > 5 ? 'bg-offline text-white' : 'bg-amber-400 text-neutral-900',
            )}
            data-testid="notifications-count"
          >
            {count > 99 ? '99+' : count}
          </span>
        ) : null}
      </button>
      {open ? (
        <div className="absolute right-0 z-50 mt-2 w-96 rounded-lg border border-white/10 bg-black/90 shadow-xl backdrop-blur">
          <div className="flex items-center justify-between border-b border-white/10 p-3">
            <h3 className="text-sm font-bold text-white">Notifications</h3>
            {count > 0 ? (
              <button
                type="button"
                onClick={handleMarkAllRead}
                className="text-xs text-info hover:underline"
                data-testid="mark-all-read"
              >
                Tout marquer lu
              </button>
            ) : null}
          </div>
          <ul className="max-h-96 overflow-y-auto">
            {listQ.isLoading ? (
              <li className="p-4 text-center text-xs text-neutral-400">Chargement…</li>
            ) : items.length === 0 ? (
              <li className="p-4 text-center text-xs text-neutral-400">
                ✓ Aucune notification.
              </li>
            ) : (
              items.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => handleClickItem(n)}
                    className={cn(
                      'flex w-full items-start gap-2 border-b border-white/5 p-3 text-left transition hover:bg-white/5',
                      !n.read_at && 'bg-white/[0.02]',
                    )}
                    data-testid={`notif-item-${n.id}`}
                  >
                    <span
                      className={cn(
                        'mt-1 inline-block h-2 w-2 flex-shrink-0 rounded-full',
                        SEVERITY_DOT[n.severity],
                      )}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p
                          className={cn(
                            'truncate text-sm',
                            n.read_at ? 'text-neutral-400' : 'font-semibold text-white',
                          )}
                        >
                          {n.title}
                        </p>
                        <span
                          className={cn(
                            'flex-shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase',
                            SEVERITY_BG[n.severity],
                          )}
                        >
                          {n.severity}
                        </span>
                      </div>
                      {n.body ? (
                        <p className="mt-0.5 line-clamp-2 text-xs text-neutral-400">
                          {n.body}
                        </p>
                      ) : null}
                      <p className="mt-0.5 text-[10px] text-neutral-500">
                        {fmtRelative(n.created_at)}
                      </p>
                    </div>
                  </button>
                </li>
              ))
            )}
          </ul>
          <div className="border-t border-white/10 p-2 text-center">
            <button
              type="button"
              onClick={() => {
                setOpen(false)
                navigate('/notifications-pharmacien')
              }}
              className="text-xs text-info hover:underline"
            >
              Voir toutes les notifications →
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}
